// src/components/search/SearchModal.tsx

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  InputAdornment,
  Badge,
} from '@mui/material';
import { Close, Search as SearchIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { performSearch } from '@/features/search/searchThunks';
import { setQuery, setActiveTab, clearSearch } from '@/features/search/searchSlice';
import {
  selectSearchQuery,
  selectSearchLoading,
  selectActiveTab,
  selectNewsResults,
  selectEventsResults,
  selectInstitutionalResults,
  selectSitesResults,
  selectFormsResults,
  selectTotalResults,
} from '@/features/search/searchSelectors';
import { SearchTab } from '@/types/search.types';
import SearchResults from './SearchResults';
import { useAuth } from '@/hooks/useAuth';
import { canAccessDocuments } from '@/utils/permissions';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectSearchQuery);
  const loading = useAppSelector(selectSearchLoading);
  const activeTab = useAppSelector(selectActiveTab);
  const newsResults = useAppSelector(selectNewsResults);
  const eventsResults = useAppSelector(selectEventsResults);
  const institutionalResults = useAppSelector(selectInstitutionalResults);
  const sitesResults = useAppSelector(selectSitesResults);
  const formsResults = useAppSelector(selectFormsResults);
  const totalResults = useAppSelector(selectTotalResults);

  const { userRole } = useAuth();
  const hasDocumentAccess = canAccessDocuments(userRole ?? undefined);

  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (!open) {
      // Reset search when modal closes
      setSearchInput('');
      dispatch(clearSearch());
    }
  }, [open, dispatch]);

  const handleSearch = () => {
    if (searchInput.trim().length > 0) {
      dispatch(setQuery(searchInput));
      dispatch(performSearch({
        searchQuery: searchInput,
        userRole: userRole ?? undefined
      }));
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: SearchTab) => {
    dispatch(setActiveTab(newValue));
  };

  const handleClose = () => {
    onClose();
  };

  const allResultsCount = newsResults.length + eventsResults.length + institutionalResults.length + sitesResults.length + formsResults.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'primary.dark' }}>
            Buscar Contenido
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search Input */}
        <TextField
          fullWidth
          placeholder={
            hasDocumentAccess
              ? "Buscar en noticias, eventos, institucional, sedes y formularios..."
              : "Buscar en noticias, eventos, sedes y formularios..."
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          autoFocus
        />

        {/* Results Count */}
        {query && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalResults === 0
              ? 'No se encontraron resultados'
              : `${totalResults} resultado${totalResults !== 1 ? 's' : ''} encontrado${totalResults !== 1 ? 's' : ''}`}
          </Typography>
        )}

        {/* Tabs */}
        {query && totalResults > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                },
              }}
            >
              <Tab
                label={
                  <Badge badgeContent={allResultsCount} color="primary" sx={{ pr: 2 }}>
                    Todos
                  </Badge>
                }
                value={SearchTab.ALL}
              />
              <Tab
                label={
                  <Badge badgeContent={newsResults.length} color="primary" sx={{ pr: 2 }}>
                    Noticias
                  </Badge>
                }
                value={SearchTab.NEWS}
              />
              <Tab
                label={
                  <Badge badgeContent={eventsResults.length} color="primary" sx={{ pr: 2 }}>
                    Eventos
                  </Badge>
                }
                value={SearchTab.EVENTS}
              />
              {hasDocumentAccess && (
                <Tab
                  label={
                    <Badge badgeContent={institutionalResults.length} color="primary" sx={{ pr: 2 }}>
                      Institucional
                    </Badge>
                  }
                  value={SearchTab.INSTITUTIONAL}
                />
              )}
              <Tab
                label={
                  <Badge badgeContent={sitesResults.length} color="primary" sx={{ pr: 2 }}>
                    Sedes
                  </Badge>
                }
                value={SearchTab.SITES}
              />
              <Tab
                label={
                  <Badge badgeContent={formsResults.length} color="primary" sx={{ pr: 2 }}>
                    Formularios
                  </Badge>
                }
                value={SearchTab.FORMS}
              />
            </Tabs>
          </Box>
        )}

        {/* Search Results */}
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : query ? (
            <SearchResults activeTab={activeTab} onClose={handleClose} />
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <SearchIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                Ingresa un término de búsqueda para comenzar
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: '80%' }}>
                Puedes buscar contenido en:
              </Typography>
              <Box component="ul" sx={{ mt: 1, textAlign: 'left', color: 'text.secondary' }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Noticias:</strong> Artículos y publicaciones académicas
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Eventos:</strong> Actividades, jornadas y fechas importantes
                </Typography>
                {hasDocumentAccess && (
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Institucional:</strong> Documentos oficiales y reglamentos
                  </Typography>
                )}
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Sedes:</strong> Información sobre ubicaciones y campus
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Formularios:</strong> Formularios y documentos descargables
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
